// components/profile/ProfileForm.jsx
import React, { useState } from 'react';
import { ProfileFormProvider } from './context/ProfileFormContext';
import { BasicInfoSection } from './sections/BasicInfoSection';
import { AboutYouSection } from './sections/AboutYouSection';
import { PortfolioSection } from './sections/PortfolioSection';
import StorefrontPreview from './StorefrontPreview';
import { useProfileForm } from './hooks/useProfileForm';
import { calculateProfileProgress } from './utils/validation';
import { 
  CheckCircleIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  BriefcaseIcon,
  SparklesIcon,
  EyeIcon,
  MapPinIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon, ArrowUpTrayIcon } from '@heroicons/react/24/solid';

const ProfileFormContent = () => {
  const [activeTab, setActiveTab] = useState('basic');
  const { 
    formData, 
    errors, 
    isDirty, 
    isActive, // NEW: Get isActive from hook
    handleSubmit, 
    loading, 
    success, 
    saveError 
  } = useProfileForm();
  
  const progress = calculateProfileProgress(formData);

  const tabs = [
    { 
      value: 'basic', 
      label: 'Basic', 
      component: BasicInfoSection, 
      icon: BuildingOfficeIcon,
      description: 'Company & contact info'
    },
    { 
      value: 'about', 
      label: 'About', 
      component: AboutYouSection, 
      icon: UserCircleIcon,
      description: 'Tell your story'
    },
    { 
      value: 'portfolio', 
      label: 'Spotlight', 
      component: PortfolioSection, 
      icon: BriefcaseIcon,
      description: 'Stand out on Boutee'
    },
    {
      value: 'preview',
      label: 'Preview (beta)',
      component: StorefrontPreview,
      icon: EyeIcon,
      description: 'Preview your storefront'
    }
  ];

  const getSectionStatus = (tabValue) => {
    const sectionFields = {
      basic: ['companyName', 'location', 'email', 'introParagraph', 'startingPrice', 'minimumTimeframe'],
      about: ['headerImage', 'firstName', 'surname', 'profilePhoto', 'title', 'bio', 'brandIcon'],
      portfolio: ['tags', 'showcaseVideo', 'galleryImages', 'awards']
    };
    
    if (tabValue === 'preview') {
      return { hasErrors: false, isComplete: false, completionPercent: 0, filledCount: 0, totalFields: 0 };
    }

    const fields = sectionFields[tabValue] || [];
    const filledCount = fields.filter(field => {
      const value = formData[field];
      if (field === 'tags') return value && Object.values(value).some(v => v === true);
      return !!value;
    }).length;
    
    const hasErrors = fields.some(field => errors[field]);
    const isComplete = filledCount === fields.length;
    const completionPercent = fields.length > 0 ? (filledCount / fields.length) * 100 : 0;
    
    return { hasErrors, isComplete, completionPercent, filledCount, totalFields: fields.length };
  };

  const ActiveTabComponent = tabs.find(tab => tab.value === activeTab)?.component;
  const userInitials = formData.firstName && formData.surname 
    ? `${formData.firstName[0]}${formData.surname[0]}`.toUpperCase()
    : formData.companyName 
      ? formData.companyName.split(' ').map(word => word[0]).join('').slice(0, 2).toUpperCase()
      : 'JW';

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--Surface-Secondary)' }}>
      <div className="max-w-7xl mx-auto">
        {/* NEW: Status Banner - Shows setup mode or live incomplete warning */}
        {!isActive && (
          <div className="mx-4 sm:mx-6 lg:mx-8 mt-6 mb-4 p-4 rounded-lg" style={{
            backgroundColor: 'var(--Blue-50)',
            border: '2px solid var(--Blue-200)'
          }}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <SparklesIcon className="w-5 h-5" style={{ color: 'var(--Blue-600)' }} />
              </div>
              <div>
                <h3 className="font-semibold mb-1" style={{ 
                  color: 'var(--Blue-900)',
                  fontSize: 'var(--font-size-body3)'
                }}>
                  Setup Mode
                </h3>
                <p style={{ 
                  color: 'var(--Blue-800)',
                  fontSize: 'var(--font-size-body4)'
                }}>
                  You can save your progress at any time with any fields empty. All fields will be required before going live on Boutee.
                </p>
              </div>
            </div>
          </div>
        )}

        {isActive && progress < 100 && (
          <div className="mx-4 sm:mx-6 lg:mx-8 mt-6 mb-4 p-4 rounded-lg" style={{
            backgroundColor: 'var(--Orange-50)',
            border: '2px solid var(--Orange-200)'
          }}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <ExclamationTriangleIcon className="w-5 h-5" style={{ color: 'var(--Orange-600)' }} />
              </div>
              <div>
                <h3 className="font-semibold mb-1" style={{ 
                  color: 'var(--Orange-900)',
                  fontSize: 'var(--font-size-body3)'
                }}>
                  Profile Incomplete
                </h3>
                <p style={{ 
                  color: 'var(--Orange-800)',
                  fontSize: 'var(--font-size-body4)'
                }}>
                  You're live on Boutee but your profile is incomplete. Complete all required fields for the best visibility.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Profile Header Card - Using gradient-primary variable with fully rounded corners */}
        <div className="shadow-xl" style={{ 
          background: 'var(--Surface-Primary)',
          borderRadius: 'var(--radius-xl)'
        }}>
          <div className="px-8 py-10">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              {/* Profile Photo */}
              <div className="relative group">
                <div className="w-24 h-24 md:w-32 md:h-32 overflow-hidden shadow-xl" style={{
                  borderRadius: 'var(--radius-l)',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(8px)',
                  border: '4px solid rgba(255, 255, 255, 0.3)'
                }}>
                  {formData.profilePhoto ? (
                    <img 
                      src={formData.profilePhoto} 
                      alt={formData.companyName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-white/30 to-white/10 
                                  flex items-center justify-center">
                      <span className="text-3xl md:text-4xl font-bold" style={{ 
                        color: 'var(--Grey-800)',
                        fontWeight: 'var(--font-weight-h3)'
                      }}>
                        {userInitials}
                      </span>
                    </div>
                  )}
                </div>
              
              </div>

              {/* Profile Info */}
              <div className="flex-1">
                <h1 className="mb-2" style={{ 
                  fontSize: 'var(--font-size-h3)',
                  fontWeight: 'var(--font-weight-h3)',
                  lineHeight: 'var(--line-height-h3)',
                  color: 'var(--Grey-800)'
                }}>
                  {formData.companyName || 'Your Business Name'}
                </h1>
                
                <div className="flex flex-wrap items-center gap-4 mb-4" style={{ 
                  color: 'var(--Grey-800)',
                  fontSize: 'var(--font-size-body3)',
                }}>
                  {formData.location && (
                    <div className="flex items-center gap-1">
                      <MapPinIcon className="w-4 h-4" />
                      <span>{formData.location}</span>
                    </div>
                  )}
                  {formData.email && (
                    <div className="flex items-center gap-1">
                      <EnvelopeIcon className="w-4 h-4" />
                      <span>{formData.email}</span>
                    </div>
                  )}
                  {formData.firstName && formData.surname && (
                    <div className="flex items-center gap-1">
                      <UserCircleIcon className="w-4 h-4" />
                      <span>{formData.firstName} {formData.surname}</span>
                    </div>
                  )}
                </div>

                {/* Progress Bar - Only show if not complete */}
                {progress < 100 && (
                  <div className="max-w-md">
                    <div className="flex justify-between items-center mb-2">
                      <span style={{ 
                        fontSize: 'var(--font-size-body4)',
                        color: 'var(--Text-Contrast)',
                        opacity: 0.9
                      }}>Profile Completion</span>
                      <span style={{ 
                        fontSize: 'var(--font-size-body4)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--Text-Contrast)'
                      }}>{Math.round(progress)}%</span>
                    </div>
                    <div className="h-2 overflow-hidden" style={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: 'var(--radius-pill)'
                    }}>
                      <div 
                        className="h-full transition-all duration-500 ease-out"
                        style={{ 
                          backgroundColor: 'var(--Brand-White)',
                          width: `${progress}%`,
                          borderRadius: 'var(--radius-pill)'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="px-4 sm:px-6 lg:px-8 -mt-6">
          {/* Tab Navigation Card */}
          <div className="mb-6 overflow-hidden" style={{ 
            backgroundColor: 'var(--Surface-Primary)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-nav-slider)'
          }}>
            <div className="grid grid-cols-1 md:grid-cols-4">
              {tabs.map((tab) => {
                const { hasErrors, isComplete, completionPercent, filledCount, totalFields } = getSectionStatus(tab.value);
                const Icon = tab.icon;
                const isTabActive = activeTab === tab.value;
                
                return (
                  <button
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                    className={`relative p-6 text-left transition-all duration-200 flex items-center
                              ${isTabActive 
                                ? '' 
                                : 'hover:bg-gray-50'}`}
                    style={{
                      backgroundColor: isTabActive ? 'var(--Purple-50)' : 'var(--Grey-50)',
                      borderBottom: isTabActive ? '4px solid var(--Purple-700)' : '4px solid transparent',
                      borderRight: tab.value !== 'portfolio' ? '1px solid var(--Stroke-Light)' : 'none'
                    }}
                  >
                    {/* Tab Content */}
                    <div className="flex items-center gap-4 w-full">
                      {/* Icon with Status */}
                      <div className="relative">
                        <div className="w-12 h-12 flex items-center justify-center" style={{
                          borderRadius: 'var(--radius-s)',
                          background: isTabActive 
                            ? 'var(--brand-gradient-1)'
                            : isComplete 
                              ? 'var(--Blue-100)'
                              : 'var(--Blue-100)',
                          color: isTabActive 
                            ? 'var(--Text-Contrast)'
                            : isComplete
                              ? 'var(--Grey-900)'
                              : 'var(--Grey-800)'
                        }}>
                          <Icon className="w-6 h-6" />
                        </div>
                        
                        {/* Status Badge */}
                        {isComplete && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center" style={{
                            backgroundColor: 'var(--Green-400)',
                            borderRadius: 'var(--radius-pill)'
                          }}>
                            <CheckCircleIcon className="w-3 h-3" style={{ color: 'var(--Grey-900)' }} />
                          </div>
                        )}
                        {hasErrors && !isComplete && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center" style={{
                            backgroundColor: 'var(--Red-600)',
                            borderRadius: 'var(--radius-pill)'
                          }}>
                            <ExclamationTriangleIcon className="w-3 h-3" style={{ color: 'var(--Text-Contrast)' }} />
                          </div>
                        )}
                      </div>

                      {/* Text Content */}
                      <div className="flex-1">
                        <h3 className="mb-1" style={{ 
                          fontWeight: 'var(--font-weight-subtitle2)',
                          fontSize: 'var(--font-size-subtitle2)',
                          lineHeight: 'var(--line-height-subtitle2)',
                          color: isTabActive ? 'var(--Purple-700)' : 'var(--Grey-800)'
                        }}>
                          {tab.label}
                        </h3>
                        <p className="mb-2" style={{ 
                          fontSize: 'var(--font-size-body4)',
                          color: 'var(--Text-Secondary)'
                        }}>
                          {tab.description}
                        </p>
                        
                        {/* Mini Progress Bar - Only show if profile not complete */}
                        {progress < 100 && totalFields > 0 && (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 overflow-hidden" style={{
                              backgroundColor: 'var(--Grey-200)',
                              borderRadius: 'var(--radius-pill)'
                            }}>
                              <div 
                                className="h-full transition-all duration-500"
                                style={{ 
                                  width: `${completionPercent}%`,
                                  background: isComplete 
                                    ? 'var(--Green-400)'
                                    : isTabActive 
                                      ? 'var(--brand-gradient-1)'
                                      : 'var(--Grey-400)',
                                  borderRadius: 'var(--radius-pill)'
                                }}
                              />
                            </div>
                            <span style={{ 
                              fontSize: 'var(--font-size-body5)',
                              color: 'var(--Text-Tertiary)'
                            }} className="whitespace-nowrap">
                              {filledCount}/{totalFields}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Active Tab Content */}
          <div className="animate-fadeIn">
            {ActiveTabComponent && <ActiveTabComponent />}
          </div>
        </div>

        {/* Sticky Save Bar */}
        {isDirty && (
          <div className="fixed bottom-0 left-0 right-0 z-50" style={{
            backgroundColor: 'var(--Surface-Primary)',
            borderTop: '1px solid var(--Stroke-Secondary)',
            boxShadow: 'var(--shadow-popup)'
          }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-3">
                  {saveError ? (
                    <>
                      <ExclamationTriangleIcon className="w-5 h-5" style={{ color: 'var(--Red-600)' }} />
                      <span style={{ 
                        fontSize: 'var(--font-size-body4)',
                        color: 'var(--Text-Error)'
                      }}>{saveError}</span>
                    </>
                  ) : success ? (
                    <>
                      <CheckCircleIcon className="w-5 h-5" style={{ color: 'var(--Green-600)' }} />
                      <span style={{ 
                        fontSize: 'var(--font-size-body4)',
                        color: 'var(--Text-Success)'
                      }}>
                        {/* NEW: Different success message based on active status */}
                        {isActive 
                          ? 'Profile updated successfully!' 
                          : 'Progress saved! Your profile will be visible once you go live on Boutee.'}
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="w-3 h-3 rounded-full animate-pulse" style={{ 
                        backgroundColor: 'var(--Purple-500)'
                      }} />
                      <span style={{ 
                        fontSize: 'var(--font-size-body4)',
                        color: 'var(--Text-Primary)',
                        fontWeight: 'var(--font-weight-medium)'
                      }}>You have unsaved changes</span>
                    </>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <button 
                    onClick={() => window.location.reload()} 
                    disabled={loading}
                    className="px-6 py-2 transition-colors"
                    style={{
                      color: 'var(--Text-Primary)',
                      backgroundColor: 'transparent',
                      borderRadius: 'var(--button-radius)',
                      fontWeight: 'var(--font-weight-medium)',
                      fontSize: 'var(--font-size-body4)'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--Grey-100)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleSubmit} 
                    disabled={loading}
                    className="px-6 py-2 text-white transition-all
                             disabled:opacity-50 disabled:cursor-not-allowed
                             flex items-center gap-2"
                    style={{
                      background: 'var(--brand-gradient-1)',
                      borderRadius: 'var(--button-radius)',
                      fontWeight: 'var(--font-weight-medium)',
                      fontSize: 'var(--font-size-body4)',
                      boxShadow: 'var(--shadow-frame-small)'
                    }}
                    onMouseEnter={(e) => e.target.style.boxShadow = 'var(--shadow-popup)'}
                    onMouseLeave={(e) => e.target.style.boxShadow = 'var(--shadow-frame-small)'}
                  >
                    {loading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        {/* NEW: Different button text based on active status */}
                        {isActive ? 'Save All Changes' : 'Save Progress'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ProfileForm = ({ jeweller }) => {
  // NEW: Determine if jeweller is active
  // If active field is missing or false, jeweller is in setup mode
  const isActive = jeweller?.active === true;

  return (
    <ProfileFormProvider initialData={jeweller} isActive={isActive}>
      <ProfileFormContent />
    </ProfileFormProvider>
  );
};

export default ProfileForm;
